import { Injectable } from '@angular/core'

import { DataConfigurationPieceModel, TemplateLibraryPieceModel } from 'src/app/components/template-library/models/template-library.model'
import { LANGUAGE_CODE_BY_LANGUAGE_ID, LANGUAGE_IDS } from 'src/app/constants/internationalized-constants-en'
import {
  ComponentModel,
  DataConfigurationModel,
  EvirLanguageDictionary,
  ReflectCountModel,
  TreeDataType,
  TreeNode,
  ZoneLayoutModel
} from '../../models/tree.model'
import { LanguageDictionaryHandlingService, currentLanguageCode } from '../language-dictionary-handling/language-dictionary-handling.service'

@Injectable({
  providedIn: 'root'
})
export class TreeBuildingService {

  constructor(private langDictionaryService: LanguageDictionaryHandlingService) { }

  buildTree(data: DataConfigurationModel[], level: number, parentId: string = '0', nodeType = TreeDataType.CONFIGURATION): TreeNode[] {
    return data.reduce<TreeNode[]>((accumulator, rawNode, index) => {
      const node = new TreeNode()
      node.id = `${parentId}/${index}`
      node.name = rawNode.name
      node.value = rawNode.value
      node.reflectCount = rawNode.reflectCount
      node.nodeType = nodeType
      if (rawNode.data) {
        node.children = this.buildTree(rawNode.data, level + 1, node.id, nodeType)
      }
      return accumulator.concat(node)
    }, [])
  }

  buildPieceTree(data: DataConfigurationPieceModel[], level: number, parentId: string = '0', nodeType = TreeDataType.TEMPLATE_LIBRARY): TreeNode[] {
    return data.map((item, index) => {
      const node = new TreeNode()
      node.id = `${parentId}/${index}`
      node.name = item.name
      node.displayName = item.pieceName
      node.value = item.value
      node.nodeType = nodeType
      if (item.data) {
        node.children = this.buildPieceTree(item.data, level + 1, node.id, nodeType)
      }
      return node
    })
  }

  getConfigurations(configurations: ZoneLayoutModel[], languageDictionary: EvirLanguageDictionary): any[] {
    if (!configurations || !configurations.length) {
      return []
    }

    if (!languageDictionary) {
      return []
    }

    const result = configurations.map(config => {
      const componentReflectCounts: ReflectCountModel[] = (config.components || []).map((item, index) => {
        return {
          index: index,
          count: 0
        }
      })
      const configName = this.langDictionaryService.convertLangKeyToString(
        config.zoneLayoutLangKey, languageDictionary)
      const zones = config.configZones.map(zone => {
        const zoneName = this.langDictionaryService.convertLangKeyToString(
          zone.tagLangKey, languageDictionary)
        const components = zone.tagComponents.map((componentIndex) => {
          const componentReflectCount = componentReflectCounts.find(x => x.index === componentIndex)
          const componentConfiguration = config.components[componentIndex] || {} as ComponentModel
          const componentName = this.langDictionaryService.convertLangKeyToString(componentConfiguration.componentLangKey, languageDictionary)
          if (componentReflectCount) { componentReflectCount.count++ }
          const conditions = (componentConfiguration.suggestedConditionLangKeys || []).map((condition, conditionIndex) => {
            return {
              name: this.langDictionaryService.convertLangKeyToString(
                condition, languageDictionary),
              data: null,
              value: {
                [conditionIndex]: condition
              },
              reflectCount: componentReflectCount
            }
          })
          return {
            name: componentName,
            data: conditions,
            reflectCount: componentReflectCount,
            value: {
              index: componentIndex,
              nameKey: componentConfiguration.componentLangKey,
              severityMax: componentConfiguration.maxSeverity,
              severityMin: componentConfiguration.minSeverity,
            }
          }
        })
        return {
          name: zoneName,
          data: components,
          value: {
            tagLangKey: zone.tagLangKey,
            tagType: this.langDictionaryService.convertLangKeyToString(zone.tagTypeLangKey, languageDictionary),
            tagNumber: zone.tagNumber,
            zoneInspectionTypes: zone.zoneInspectionTypes ? zone.zoneInspectionTypes : [],
            assetViewLocation: zone.assetViewLocation,
          }
        }
      })
      return {
        name: configName,
        data: zones,
        value: {
          assetType: config.assetType,
          components: config.components,
          zoneLayoutLangKey: config.zoneLayoutLangKey,
          assetViewGrid: config.assetViewGrid,
          assetViewId: config.assetViewId,
          zoneLayoutId: config.zoneLayoutId,
          borderConfigImport: config.borderConfigImport
        }
      }
    })
    return result
  }

  getPieces(pieces: TemplateLibraryPieceModel[], languageDictionary: EvirLanguageDictionary): DataConfigurationPieceModel[] {
    if (!(pieces && pieces.length) || !languageDictionary) {
      return []
    }

    return pieces.map(({ id, name, piece }) => {
      const convertKeyToName = key => this.langDictionaryService.convertLangKeyToString(key, languageDictionary, currentLanguageCode)
        || this.langDictionaryService.convertLangKeyToString(key, languageDictionary, LANGUAGE_CODE_BY_LANGUAGE_ID[LANGUAGE_IDS.ENGLISH])
      const config = piece as any
      // Handle both API formats: nameLangKey (old IB API) or zoneLayoutLangKey (new EVIR API)
      const configname = convertKeyToName(config.nameLangKey)
        || convertKeyToName(config.zoneLayoutLangKey)
        || config.zoneLayoutLanguageKey
        || name
      const zones = (config.configZones || []).map(zone => {
        // Handle both formats: tagLangKey (new) or tagNameKey (old)
        const zoneName = convertKeyToName(zone.tagLangKey) || zone.tagNameKey || ''
        const components = (zone.tagComponents || []).map((tagComponent) => {
          // Handle both formats: tagComponent can be an index (number) or a component object
          const component = typeof tagComponent === 'number'
            ? (config.components || [])[tagComponent]
            : tagComponent as unknown as ComponentModel
          if (!component) {
            return null
          }
          // Handle both formats: componentLangKey (new) or componentNameKey (old)
          const componentName = convertKeyToName(component.componentLangKey) || component.componentNameKey || ''
          const conditions = (component.suggestedConditionLangKeys || []).map((condition) => {
            return {
              name: convertKeyToName(condition),
              pieceName: null,
              data: null,
            }
          })
          return {
            name: componentName,
            pieceName: null,
            data: conditions,
          }
        }).filter(Boolean)
        return {
          name: zoneName,
          pieceName: null,
          data: components,
          value: {
            tagType: convertKeyToName(zone.tagTypeLangKey),
            tagNumber: zone.tagNumber,
            zoneInspectionTypes: zone.zoneInspectionTypes ? zone.zoneInspectionTypes : [],
          }
        }
      })
      return {
        name: configname,
        pieceName: name,
        data: zones,
        value: { id, piece },
      }
    })
  }

  findNodeSiblings(nodes: Array<TreeNode>, id: string): Array<TreeNode> {
    let result, subResult
    nodes.forEach((item) => {
      if (item.id === id) {
        result = nodes
      } else if (item.children) {
        subResult = this.findNodeSiblings(item.children, id)
        if (subResult) { result = subResult }
      }
    })
    return result
  }
}
